import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  Rocket, CheckCircle2, ArrowRight, Sparkles, Target, Brain,
  FileText, TrendingUp, Users, Clock, Star, Shield, Zap,
  ChevronRight, Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';

import Footer from '@/components/Footer';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { MyProposalsSection } from '@/components/launch-kit/MyProposalsSection';

const DELIVERABLES = [
  { icon: Target, title: 'Personalized Business Concept', desc: 'Custom niche validation tailored to your strengths and market opportunity' },
  { icon: Sparkles, title: 'Complete Branding Package', desc: 'Name, positioning, bio, and brand identity ready to launch' },
  { icon: FileText, title: '30 Content Ideas + Scripts', desc: 'A full month of content planned and scripted for you' },
  { icon: TrendingUp, title: '30-Day Execution Plan', desc: 'Step-by-step daily actions — no guessing what to do next' },
  { icon: Brain, title: 'Monetization Strategy', desc: 'Exactly how to make your first money with your business' },
  { icon: Zap, title: 'Platform Setup Guidance', desc: 'Technical setup simplified — what goes where and why' },
  { icon: Rocket, title: 'Growth Roadmap', desc: 'Scaling blueprint from launch to your first $10K month' },
];

const STEPS = [
  { step: '01', title: 'Complete a Short Onboarding Form', desc: 'Tell us about your goals, interests, and skills. Takes under 5 minutes.' },
  { step: '02', title: 'We Build Your Business Blueprint', desc: 'Our team creates your personalized AI business plan from scratch.' },
  { step: '03', title: 'Receive Your Launch Kit', desc: 'Your complete, ready-to-execute business blueprint delivered to your inbox.' },
];

const WHO_FOR = [
  'Beginners who don\'t know where to start',
  'People who want results without months of trial and error',
  'Anyone who prefers execution over theory',
  'Busy professionals who value their time',
  'Those who want a clear, proven path to income',
];

const LaunchKit = () => {
  const { user, session } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleCheckout = async () => {
    if (!user || !session) {
      navigate('/login?redirect=/launch-kit');
      return;
    }

    setIsLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('create-launch-kit-checkout', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });

      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to start checkout';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>AI Business Launch Kit — We Build Your Business For You</title>
        <meta name="description" content="Receive a fully personalized, ready-to-launch AI business blueprint created by our team. Done-for-you service delivered within 14 days." />
      </Helmet>
      <LightBackground />

      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="text-lg font-semibold text-foreground">
              Faceless Aura <span className="text-primary">Club</span>
            </span>
          </Link>
          <div className="flex items-center gap-2">
            {user && (
              <Button
                asChild
                size="sm"
                variant="outline"
                className="hidden sm:inline-flex"
              >
                <Link to="/launch-kit/status">
                  <FileText className="w-4 h-4 mr-1.5" />
                  My Proposals
                </Link>
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => navigate('/launch-kit/brief')}
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-semibold"
            >
              Get My Business Built
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-accent/10 text-accent px-4 py-1.5 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Premium Done-For-You Service
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6 leading-[1.1]">
              We Build Your AI Business{' '}
              <span className="bg-gradient-to-r from-accent to-primary bg-clip-text text-transparent">
                For You
              </span>
              <br />
              <span className="text-3xl sm:text-4xl lg:text-5xl font-medium text-muted-foreground">
                Ready to Execute
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-8 leading-relaxed">
              Receive a fully personalized, ready-to-launch digital business blueprint 
              created by our team — delivered to your inbox within 14 days.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <Button
                size="lg"
                onClick={() => navigate('/launch-kit/brief')}
                className="bg-accent hover:bg-accent/90 text-accent-foreground font-bold text-lg px-10 py-6 rounded-xl shadow-lg shadow-accent/20 hover:shadow-xl hover:shadow-accent/30 transition-all"
              >
                <Rocket className="w-5 h-5 mr-2" />
                Get My Business Built — from $1,799
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-primary" /> 100% Personalized</span>
              <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-primary" /> 14-Day Delivery</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-primary" /> Premium Quality</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* My Proposals (only shows if user is logged in & has briefs) */}
      <MyProposalsSection />

      {/* What You Get */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-card/50 border-y border-border/30">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Everything You Get Inside Your{' '}
              <span className="text-accent">Launch Kit</span>
            </h2>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              A complete, ready-to-execute business plan — not a generic template.
              Every element is crafted specifically for you.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {DELIVERABLES.map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="p-6 rounded-2xl bg-card border border-border/40 hover:border-accent/30 hover:shadow-lg hover:shadow-accent/5 transition-all group"
              >
                <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                  <item.icon className="w-6 h-6 text-accent" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-14"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              How It Works
            </h2>
            <p className="text-muted-foreground text-lg">Simple. Fast. Done for you.</p>
          </motion.div>

          <div className="space-y-8">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="flex items-start gap-6 p-6 rounded-2xl bg-card border border-border/40"
              >
                <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-accent to-primary flex items-center justify-center">
                  <span className="text-xl font-bold text-white">{step.step}</span>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-1">{step.title}</h3>
                  <p className="text-muted-foreground">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Different */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-accent/5 to-transparent">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              This Is <span className="text-accent">Not</span> Another Course
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8"
          >
            <div className="p-8 rounded-2xl bg-destructive/5 border border-destructive/20">
              <h3 className="text-lg font-bold mb-4 text-destructive">Most Programs</h3>
              <ul className="space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">✗</span>
                  Give you hours of theory to figure out
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">✗</span>
                  Leave you guessing what to do first
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">✗</span>
                  Generic templates for everyone
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-destructive mt-0.5">✗</span>
                  Months before you see any results
                </li>
              </ul>
            </div>
            <div className="p-8 rounded-2xl bg-accent/5 border border-accent/20">
              <h3 className="text-lg font-bold mb-4 text-accent">Your Launch Kit</h3>
              <ul className="space-y-3 text-sm text-foreground">
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  Complete business blueprint built for YOU
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  Step-by-step — Day 1 to Day 30 planned out
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  100% personalized to your niche and goals
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-accent mt-0.5 flex-shrink-0" />
                  Ready to execute the moment you receive it
                </li>
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Who This Is For
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-xl mx-auto"
          >
            <div className="space-y-4">
              {WHO_FOR.map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border/40">
                  <CheckCircle2 className="w-5 h-5 text-accent flex-shrink-0" />
                  <span className="text-foreground font-medium">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Delivery Note */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-card border border-border/40"
          >
            <Clock className="w-8 h-8 text-accent mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-2">Premium Custom Service</h3>
            <p className="text-muted-foreground leading-relaxed">
              Because every Launch Kit is built from scratch by our team specifically for you, 
              delivery takes up to <strong className="text-foreground">14 business days</strong>. 
              Your complete blueprint will be delivered directly to your email.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Ready to Have Your Business{' '}
              <span className="text-accent">Built For You?</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              Stop guessing. Stop watching more videos. 
              Get a complete, personalized business plan delivered to you.
            </p>
            <Button
              size="lg"
              onClick={() => navigate('/launch-kit/brief')}
              className="bg-accent hover:bg-accent/90 text-accent-foreground font-bold text-lg px-12 py-7 rounded-xl shadow-lg shadow-accent/20 hover:shadow-xl hover:shadow-accent/30 transition-all"
            >
              <Rocket className="w-5 h-5 mr-2" />
              Get My Business Built — from $1,799
            </Button>
            <div className="mt-6 flex items-center justify-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Shield className="w-4 h-4" /> Secure Payment</span>
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4" /> Done-For-You</span>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LaunchKit;
